import { useCallback, memo } from 'react';
import { Box, Button, Flex, Badge, Progress, useMultiStyleConfig } from '@chakra-ui/react';
// FontAwesome icons imported for shared Icon component
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FileItem, FileUploadSize } from './types/types';
import { FILE_UPLOAD_CONSTANTS, FILE_STATUS, FileStatus } from './utils/constants';
import { IconButtonWithTooltip } from '../../molecules/IconButtonWithTooltip';
import FileStatusIcon from './FileStatusIcon';

/**
 * Maps file status to FileUpload theme variant
 */
const mapStatusToVariant = (status: FileStatus): string => {
  const variantMap: Record<FileStatus, string> = {
    ready: 'pending',
    uploading: 'uploading',
    uploaded: 'completed',
    error: 'error',
    cancelled: 'cancelled',
  };

  return variantMap[status] || 'pending';
};

type FileListItemProps = {
  file: FileItem;
  onRemove: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
  isDisabled: boolean;
  showFileSize?: boolean;
  allowRetry?: boolean;
  size?: FileUploadSize;
};

const FileListItem = memo(
  ({ file, onRemove, onRetry, isDisabled, showFileSize, allowRetry, size = 'md' }: FileListItemProps) => {
    // Get theme variant based on file status
    const variant = mapStatusToVariant(file.status);
    const styles = useMultiStyleConfig('FileUpload', { size, variant });

    const handleRemove = useCallback(() => {
      onRemove(file.id);
    }, [file.id, onRemove]);

    const handleRetry = useCallback(() => {
      if (onRetry) {
        onRetry(file.id);
      }
    }, [file.id, onRetry]);

    const getStatusBadgeProps = useCallback(() => {
      switch (file.status) {
        case FILE_STATUS.UPLOADING:
          return {
            colorScheme: 'blue',
            children: 'Uploading',
          };
        case FILE_STATUS.UPLOADED:
          return {
            colorScheme: 'green',
            children: 'Uploaded',
          };
        case FILE_STATUS.ERROR:
          return {
            colorScheme: 'red',
            variant: 'solid',
            children: 'Failed',
          };
        case FILE_STATUS.CANCELLED:
          return {
            colorScheme: 'gray',
            variant: 'solid',
            children: 'Cancelled',
          };
        default:
          return {
            colorScheme: 'gray',
            variant: 'outline',
            children: 'Ready',
          };
      }
    }, [file.status]);

    const formatFileSize = useCallback((bytes: number): string => {
      if (isNaN(bytes) || bytes < 0) return '0 B';

      if (bytes < 1024) return `${bytes} B`;

      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }, []);

    const getProgressColorScheme = useCallback(() => {
      switch (file.status) {
        case FILE_STATUS.UPLOADED:
          return 'green';
        case FILE_STATUS.UPLOADING:
          return 'blue';
        default:
          return 'gray';
      }
    }, [file.status]);

    return (
      <Box position={'relative'} borderRadius={'base'} overflow={'hidden'}>
        <Flex
          bgColor={'chakra-body-bg'}
          borderRadius={'base'}
          borderWidth={1}
          borderColor={file.error && 'error'}
          gap={1}
          px={3}
          py={'7px'}
        >
          <Flex gap={1} py={1} flexGrow={1} alignItems={'start'}>
            <Box py={0.5}>
              <FileStatusIcon status={file.status} />
            </Box>

            <Box __css={styles.fileName} flexGrow={1} wordBreak={'break-word'}>
              {file.name}

              {showFileSize && file.size > 0 && (
                <Box as="span" color={'muted'} __css={styles.fileSize}>
                  {' '}
                  ({formatFileSize(file.size)})
                </Box>
              )}

              {file.error && (
                <Box fontSize={'sm'} color={'error'}>
                  {file.error}
                  {file.retryCount && file.retryCount > 0 && (
                    <>
                      {' '}
                      (Tried {file.retryCount} time{file.retryCount > 1 ? 's' : ''})
                    </>
                  )}

                  {allowRetry && file.status === FILE_STATUS.ERROR && onRetry && (
                    <Button variant={'link'} size={'sm'} ml={2} isDisabled={isDisabled} onClick={handleRetry}>
                      Retry
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Flex>

          <Box py={0.5}>
            <Badge size={'sm'} {...getStatusBadgeProps()} />
          </Box>

          <Flex>
            <IconButtonWithTooltip
              label="Remove file"
              onClick={handleRemove}
              isDisabled={isDisabled || file.status === FILE_STATUS.UPLOADING}
              iconProps={{ icon: faTrashCan }}
              buttonProps={{
                size: 'sm',
                variant: 'ghost',
                colorScheme: 'red',
              }}
            />
          </Flex>
        </Flex>

        {(file.status === FILE_STATUS.UPLOADING || file.status === FILE_STATUS.UPLOADED) && (
          <Progress
            aria-label={FILE_UPLOAD_CONSTANTS.ARIA_LABELS.PROGRESS(file.name, file.progress)}
            value={file.status === FILE_STATUS.UPLOADED ? 100 : file.progress}
            colorScheme={getProgressColorScheme()}
            position="absolute"
            height={1}
            bottom={0}
            left={0}
            right={0}
          />
        )}
      </Box>
    );
  },
);

FileListItem.displayName = 'FileListItem';
export default FileListItem;
